import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiUri } from "../../utilities/apiManager";

const initialState = {
  data: [],
  itemsCount: 0,
  pages: 0,
  isLoading: false,
  error: null,

  filter: {},
  page: 1,
  query: "",
};

export const getPropertyVerifications = createAsyncThunk(
  "propertyVerification/getPropertyVerifications",
  async ({ page, query, filter, params = {} }, thunkApi) => {
    try {
      const state = thunkApi.getState();
      const token = state.auth.accessToken;

      const response = await axios.get(`${apiUri}/verifications`, {
        headers: {
          "auth-token": "Bearer " + token,
        },
        params: {
          page,
          query,
          ...params,
          ...filter,
        },
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch property verifications"
      );
    }
  }
);

export const updatePropertyVerification = createAsyncThunk(
  "propertyVerification/updatePropertyVerification",
  async ({ id, data }, thunkApi) => {
    try {
      const token = thunkApi.getState().auth.accessToken;
      const headers = {
        "auth-token": "Bearer " + token,
      };

      // If data is FormData, don't set Content-Type (let browser set it with boundary)
      if (!(data instanceof FormData)) {
        headers["Content-Type"] = "application/json";
      }

      const response = await axios.put(`${apiUri}/verifications/${id}`, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to update property verification"
      );
    }
  }
);

export const createPropertyVerification = createAsyncThunk(
  "propertyVerification/createPropertyVerification",
  async (data, thunkApi) => {
    try {
      const token = thunkApi.getState().auth.accessToken;
      const response = await axios.post(`${apiUri}/verifications`, data, {
        headers: {
          "auth-token": "Bearer " + token,
        },
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to create property verification"
      );
    }
  }
);

export const confirmPropertyVerification = createAsyncThunk(
  "propertyVerification/confirmPropertyVerification",
  async ({ id, country }, thunkApi) => {
    try {
      const token = thunkApi.getState().auth.accessToken;
      const response = await axios.post(
        `${apiUri}/verifications/confirm/${id}`,
        {},
        {
          headers: {
            "auth-token": "Bearer " + token,
          },
          params: { country },
        }
      );
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to confirm property verification"
      );
    }
  }
);

export const getOtherOwnersVerifications = createAsyncThunk(
  "propertyVerification/getOtherOwnersVerifications",
  async ({ page, query }, thunkApi) => {
    try {
      const token = thunkApi.getState().auth.accessToken;
      const response = await axios.get(`${apiUri}/verifications/list`, {
        headers: {
          "auth-token": "Bearer " + token,
        },
        params: { page, query },
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch other owners verifications"
      );
    }
  }
);
export const ignorePropertyVerification = createAsyncThunk(
  "propertyVerification/ignorePropertyVerification",
  async ({ _id }, thunkApi) => {
    try {
      const token = thunkApi.getState().auth.accessToken;
      const response = await axios.post(
        `${apiUri}/verifications/${_id}/ignore`,
        {},
        {
          headers: {
            "auth-token": "Bearer " + token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to confirm property verification"
      );
    }
  }
);
const propertyVerificationSlice = createSlice({
  name: "propertyVerification",
  initialState,
  reducers: {
    resetState: () => initialState,
    clearError: (state) => {
      state.error = null;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setQuery: (state, action) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPropertyVerifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = [];
        state.itemsCount = 0;
        state.pages = 0;
      })
      .addCase(getPropertyVerifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getPropertyVerifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updatePropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        // state.error = action.payload;
      })
      .addCase(createPropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add the new item to the data array
        state.data.unshift(action.payload.data);
        state.itemsCount += 1;
      })
      .addCase(createPropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmPropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmPropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(confirmPropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getOtherOwnersVerifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = [];
        state.itemsCount = 0;
        state.pages = 0;
      })
      .addCase(getOtherOwnersVerifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getOtherOwnersVerifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(ignorePropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(ignorePropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(ignorePropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setFilter, setPage, setQuery, resetState } =
  propertyVerificationSlice.actions;
export default propertyVerificationSlice.reducer;
